import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { CoreModule } from '../../core.module';

import { OrderPromoStacks } from '../../../types/datatypes';
import { ErrorResponse } from '../../../types/error-response';
import { PromoStack, PromoStackPopulated } from '../../../types/promo-stacks';
import { OrderPromoStackResult } from '../../../types/promo-stacks/order-promo-stack-result.interface';
import { ServerConnectionService } from '../server-connection.service';
import { IAPIResponse } from '../../../types/api-response.interface';

@Injectable({
	providedIn: CoreModule,
})

export class PromoStackService {
	public statuses: ['in-progress', 'public', 'archived'];

	private _promoStackStore = <ReplaySubject<Object>> new ReplaySubject(1);

	private dataStore = {
		store: [],
	};

	constructor(
		private serverConnectionService: ServerConnectionService,
	) { }

	get promoStackStore$() {
		return this._promoStackStore.asObservable();
	}

	public getPromoStacks() {
		this.serverConnectionService.http$('GET', '/api/promostacks/list-available', null, null).subscribe(
			(promoStacks: PromoStackPopulated[]) => {
				this.dataStore.store = promoStacks;
				this._promoStackStore.next(this.dataStore.store);
			},
		);
	}

	public getPromoStackById(id: string): PromoStackPopulated {
		return this.dataStore.store.find((promoStack: PromoStack) => promoStack._id === id);
	}

	public getPromoStackBySlug(slug: string): PromoStackPopulated {
		return this.dataStore.store.find((promoStack: PromoStack) => this._kabobCase(promoStack.name) === slug || promoStack.newSlug === slug);
	}

	public getPromoStackByNameKabob(nameKabob: string): PromoStackPopulated {
		return this.dataStore.store.find((promoStack: PromoStack) => this._kabobCase(promoStack.name) === nameKabob);
	}

	public getPromoStack(id: string): Observable<Object | PromoStack | ErrorResponse> {
		return this.serverConnectionService.http$('GET', `/api/promostacks/${id}`, null, null);
	}

	public getPromoStackAvailability(id: string) {
		return this.serverConnectionService.http$('GET', `/api/promostacks/availability/${id}`, null, null);
	}

	public createPromoStack(promoStackId: string, orderId: string) {
		return new Promise<OrderPromoStacks>((resolve, reject) => this.serverConnectionService.post$(
			'/api/order-promo-stacks/create', { promoStackId, orderId },
		).subscribe({
			next: (data: OrderPromoStackResult) => resolve(data.orderPromoStacks),
			error: () => reject(),
		}));
	}

	public updatePromoStack(
		id: String,
		features: any[],
		bookId: string,
		startDate: string,
		dates: any[],
		paymentAmount: number,
		bookPriceDuringPromo: number,
	) {
		const data = { id, features, bookId, startDate, dates, paymentAmount, bookPriceDuringPromo };
		return new Promise<OrderPromoStacks>((resolve, reject) => {
			this.serverConnectionService.post$('/api/order-promo-stacks/update', data)
				.subscribe((result: OrderPromoStackResult) => resolve(result.orderPromoStacks), reject);
		});
	}

	public updateFeaturePricing(featureId: string, newPrice: number) {
		return new Promise<void>((resolve, reject) => {
			this.serverConnectionService.post$<IAPIResponse>(
				`/api/order-promo-stacks/update-feature-payment/${featureId}`,
				{ paymentAmount: newPrice },
			).subscribe({
				next: (response) => {
					if (!response.success) {
						reject();
					}
					resolve();
				},
				error: () => {
					reject();
				},
			});
		});
	}

	public updatePromoStackFeatures(updates) {
		return new Promise<OrderPromoStacks>((resolve, reject) => {
			this.serverConnectionService
				.post$('/api/order-promo-stacks/update-order-features', updates)
				.subscribe((result: OrderPromoStackResult) => resolve(result.orderPromoStacks), reject);
		});
	}

	public getOrderPromoStacksByIds(ids): Observable<Object | ErrorResponse> {
		const body = {
			ids,
		};

		return this.serverConnectionService.http$('POST', '/api/order-promo-stacks/list-by-ids', null, body);
	}

	private _kabobCase(str: string) {
		return str.toLowerCase().replace(/ /g, '-');
	}
}
